/**
 * O tour guiado, portado de tour.js.
 *
 * Ele acontece sobre o painel de verdade — recorte iluminado no elemento real,
 * uma ação concreta por passo — e não numa sequência de telas pintadas. A razão
 * é que onboarding que mostra print não ensina onde clicar; o passo daqui só
 * fecha quando a pessoa fez a coisa no lugar onde ela mora.
 *
 * Trilha por persona porque júnior e tech lead não precisam do mesmo caminho, e
 * o progresso é por usuário no servidor (também é a métrica de adoção).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { postar } from '../api/cliente';
import { useTour } from '../api/consultas';
import { useSessao } from '../auth/sessao';
import { acionavel } from '../componentes/acionavel';
import { md } from '../md/markdown';
import { PASSOS, TRILHAS, type Passo, type Trilha } from './passos';

interface Alvo { esquerda: number; topo: number; largura: number; altura: number }

export function Tour({ slug, aberto, aoFechar }: {
  slug: string;
  aberto: boolean;
  aoFechar: () => void;
}) {
  const navegar = useNavigate();
  const { eu } = useSessao();
  const progresso = useTour(slug, aberto);
  const [feitos, setFeitos] = useState<Set<string>>(new Set());
  const [trilha, setTrilha] = useState<Trilha | null>(null);
  const [i, setI] = useState(0);
  const [alvo, setAlvo] = useState<Alvo | null>(null);

  useEffect(() => {
    if (progresso.data?.done) setFeitos(new Set(progresso.data.done));
  }, [progresso.data]);

  // o progresso é registro, não requisito: se a gravação falhar, o tour segue
  const marcar = useCallback((passo: string | null, persona: string, fim: boolean) => {
    if (passo) setFeitos((s) => new Set(s).add(passo));
    void postar(`/api/${slug}/tour`, { step: passo, persona, done: fim }).catch(() => {});
  }, [slug]);

  const fechar = useCallback(() => {
    setTrilha(null);
    setI(0);
    setAlvo(null);
    aoFechar();
  }, [aoFechar]);

  useEffect(() => {
    if (!aberto) return;
    const noTeclado = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    document.addEventListener('keydown', noTeclado);
    return () => document.removeEventListener('keydown', noTeclado);
  }, [aberto, fechar]);

  const passos = trilha ? TRILHAS[trilha].steps : [];
  const chave = passos[i];
  const passo: Passo | undefined = chave ? PASSOS[chave] : undefined;

  // a aba precisa pintar antes de medirmos o alvo, como o setTimeout de
  // tour.js:149; sem isso o recorte mede o elemento da aba anterior
  useEffect(() => {
    if (!aberto || !passo) { setAlvo(null); return; }
    if (passo.aba) navegar(`/p/${slug}/${passo.aba}`);
    let vivo = true;
    const medir = () => {
      if (!vivo) return;
      const el = document.querySelector(passo.sel);
      if (!el) { setAlvo(null); return; }
      const r = el.getBoundingClientRect();
      const pad = 8;
      setAlvo({
        esquerda: r.left - pad, topo: r.top - pad, largura: r.width + pad * 2, altura: r.height + pad * 2,
      });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const t = window.setTimeout(medir, passo.aba ? 260 : 40);
    window.addEventListener('resize', medir);
    return () => {
      vivo = false;
      window.clearTimeout(t);
      window.removeEventListener('resize', medir);
    };
  }, [aberto, passo, navegar, slug]);

  if (!aberto) return null;

  if (!trilha) {
    return (
      <Balao centralizado largo>
        <h4>Tour guiado — escolha sua trilha</h4>
        <p>
          O tour acontece sobre o painel de verdade: cada passo pede uma ação real e some quando você
          faz. Dá para sair a qualquer momento.
        </p>
        <div className="persona-grid">
          {(Object.keys(TRILHAS) as Trilha[]).map((k) => {
            const t = TRILHAS[k];
            const completa = t.steps.every((s) => feitos.has(s));
            return (
              <div
                key={k}
                className={`persona${completa ? ' done' : ''}`}
                {...acionavel(() => { setTrilha(k); setI(0); marcar(null, k, false); })}
              >
                <b>{t.label}</b>
                <span>{t.hint} · {t.steps.length} passos{completa ? ' · concluída' : ''}</span>
              </div>
            );
          })}
        </div>
        <div className="tour-row">
          <span className="muted">{feitos.size} passo(s) já vistos por {eu?.email || 'você'}</span>
          <button type="button" className="btn ghost sm" onClick={fechar}>fechar</button>
        </div>
      </Balao>
    );
  }

  if (i >= passos.length) {
    return (
      <Balao centralizado>
        <h4>Trilha {TRILHAS[trilha].label} concluída</h4>
        <p>
          Agora o ciclo fecha sozinho: você trabalha no Cursor, o agente lê o acervo, registra no
          diário da sessão o que descobriu, propõe melhoria ao final e o time aprova aqui.
        </p>
        <div className="do">
          No clone: <code>eng init-agent</code> e reinicie o Cursor. No terminal:{' '}
          <code>eng session start &quot;sua tarefa&quot;</code>.
        </div>
        <div className="tour-row">
          <span className="muted">progresso guardado no seu usuário</span>
          <button type="button" className="btn ghost sm" onClick={() => { setTrilha(null); setI(0); }}>
            outra trilha
          </button>
          <button type="button" className="btn sm" onClick={fechar}>pronto</button>
        </div>
      </Balao>
    );
  }

  const st = passo!;
  const cabeEmbaixo = !!alvo && alvo.topo + alvo.altura + 16 + 260 < window.innerHeight;
  const posicao = alvo
    ? {
      transform: 'none',
      left: Math.max(12, Math.min(alvo.esquerda + 8, window.innerWidth - 410)),
      top: cabeEmbaixo ? alvo.topo + alvo.altura + 16 : Math.max(12, alvo.topo - 268),
    }
    : undefined;

  return (
    <>
      {alvo && (
        <div
          className="tour-mask show"
          id="tour-mask"
          style={{ left: alvo.esquerda, top: alvo.topo, width: alvo.largura, height: alvo.altura }}
        />
      )}
      <Balao centralizado={!alvo} posicao={posicao}>
        <div className="tour-dots">
          {passos.map((s, ix) => (
            <i key={s} className={ix === i ? 'on' : feitos.has(s) ? 'done' : ''} />
          ))}
        </div>
        <h4>{st.title}</h4>
        <div dangerouslySetInnerHTML={{ __html: md(st.text) }} />
        <div className="do">
          <b>Faça agora:</b> <span dangerouslySetInnerHTML={{ __html: md(st.doit) }} />
        </div>
        <div className="tour-row">
          <span className="muted">{i + 1} de {passos.length} · {TRILHAS[trilha].label}</span>
          {!!i && (
            <button type="button" className="btn ghost sm" onClick={() => setI(i - 1)}>voltar</button>
          )}
          <button type="button" className="btn ghost sm" onClick={fechar}>sair</button>
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              marcar(chave, trilha, false);
              if (i + 1 >= passos.length) marcar(null, trilha, true);
              setI(i + 1);
            }}
          >
            {i === passos.length - 1 ? 'terminar' : 'próximo'}
          </button>
        </div>
      </Balao>
    </>
  );
}

function Balao({ children, centralizado, largo, posicao }: {
  children: ReactNode;
  centralizado?: boolean;
  largo?: boolean;
  posicao?: { transform: string; left: number; top: number };
}) {
  const estilo = posicao
    ? { display: 'block', ...posicao }
    : {
      display: 'block',
      left: '50%',
      top: centralizado ? '18vh' : '20vh',
      transform: 'translateX(-50%)',
      ...(largo ? { width: 'min(560px,92vw)' } : {}),
    };
  return (
    <div className="tour-pop show" id="tour-pop" role="dialog" aria-modal="true" style={estilo}>
      {children}
    </div>
  );
}
